import { useEffect } from "react";
import { useSelector } from "react-redux";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useLocalState } from "../../../hooks";
import Api from "../../../store/api";

type NotesProps = {
  user: any;
};

const parseLatestNoteText = (rawNote: any) => {
  if (!rawNote) return "";
  if (typeof rawNote !== "string") return String(rawNote);

  try {
    const parsed = JSON.parse(rawNote);
    if (Array.isArray(parsed) && parsed.length) {
      const latest = parsed[parsed.length - 1];
      if (typeof latest === "string") return latest;
      if (latest && typeof latest === "object") {
        return latest.text || latest.note || rawNote;
      }
    }
    if (parsed && typeof parsed === "object") {
      return parsed.text || parsed.note || rawNote;
    }
  } catch (error) {
    return rawNote;
  }

  return rawNote;
};

export default function Notes({ user }: NotesProps) {
  const authUser = useSelector((state: any) => state.auth?.user);
  const defaultState = {
    loading: false,
    saving: false,
    note: "",
    errorMessage: "",
    successOpen: false,
  };
  const [LS, setLS] = useLocalState(defaultState);

  useEffect(() => {
    const loadNote = async () => {
      if (!authUser?.id || !user?.id) return;
      setLS({ loading: true, errorMessage: "" });
      try {
        const response = await Api.getInterest(authUser.id, user.id);
        const interest = response?.data?.data;
        setLS({ loading: false, note: parseLatestNoteText(interest?.note) });
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to load note.";
        setLS({ loading: false, errorMessage });
      }
    };

    loadNote();
  }, [authUser?.id, user?.id]);

  const handleSave = async () => {
    if (!authUser?.id || !user?.id) return;
    if (!LS.note?.trim()) {
      setLS({ errorMessage: "Please enter a note before saving." });
      return;
    }

    setLS({ saving: true, errorMessage: "" });
    try {
      await Api.sendReportOrNote("NOTE", {
        fromId: authUser.id,
        toId: user.id,
        note: LS.note.trim(),
      });
      setLS({ saving: false, successOpen: true });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to save note.";
      setLS({ saving: false, errorMessage });
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Notes
        </Typography>
        <Typography color="textSecondary" sx={{ mb: 2 }}>
          Add your private note for this profile.
        </Typography>

        {LS.loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TextField
              multiline
              minRows={4}
              fullWidth
              value={LS.note}
              onChange={(e) => setLS({ note: e.target.value })}
              placeholder="Write your note here"
            />
            {LS.errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {LS.errorMessage}
              </Alert>
            )}
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button variant="contained" onClick={handleSave} disabled={LS.saving}>
                {LS.saving ? "Saving..." : "Save Note"}
              </Button>
            </Box>
          </>
        )}

        <Snackbar
          open={LS.successOpen}
          autoHideDuration={3000}
          onClose={() => setLS({ successOpen: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={() => setLS({ successOpen: false })} severity="success" sx={{ width: "100%" }}>
            Note saved successfully.
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
}
